import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card.jsx";
import { Button } from "@/components/ui/button.jsx";
import { ShieldAlert, LogOut } from "lucide-react";
import {
  getAuthorizationStatus,
  logOut,
} from "@/api/detechtion-account-api.js";

/**
 * Unauthorized access page. To be used when the user got past Cognito, but are not authorized
 * to access OneView.
 * @returns {JSX.Element} 403 unauthorized error page
 */
export default function Unauthorized() {
  const [authStatusDetails, setAuthStatusDetails] = useState("");

  useEffect(() => {
    getAuthorizationStatus((response) => {
      const details = getAuthStatusDetails(response);
      if (details) {
        setAuthStatusDetails(details);
      }
    });
  }, []);

  const handleLogout = () => {
    logOut(() => {
      // Redirect to login page after logout
      window.location.href = import.meta.env.VITE_LAUNCHPAD_URL;
    });
  };

  const getAuthStatusDetails = (response) => {
    if (!response) {
      return "";
    }
    let statusMessage = "";
    if (!response.detechtionUsername) {
      statusMessage += "You are not logged in to Detechtion at this time.";
    } else {
      statusMessage +=
        'You are currently logged into Detechtion as "'
        + response.detechtionUsername
        + '".';
    }

    if (response.enbaseUsername) {
      statusMessage +=
        " You have OneView access across "
        + response.enbaseTenantCount
        + " tenant"
        + (response.enbaseTenantCount === 1 ? "" : "s")
        + ". Use the tenant switcher in the sidebar to choose which organization you are viewing.";

      if (
        response.detechtionUsername
        && response.enbaseUsername != response.detechtionUsername
      ) {
        statusMessage +=
          ' You are logged in to OneView as "'
          + response.enbaseUsername
          + '" which differs from your Detechtion login.';
      }
    } else if (response.detechtionUsername) {
      if (response.enbaseTenantCount == 0) {
        statusMessage +=
          " However, you do not have the necessary permissions to log in to OneView.";
      } else {
        statusMessage +=
          " You are not logged in to OneView at this time, but have access to "
          + response.enbaseTenantCount
          + " tenant"
          + (response.enbaseTenantCount === 1 ? "" : "s")
          + " once you complete OneView sign-in.";

        if (!response.enbaseEULAAccepted) {
          statusMessage += " You will also need to accept the EULA.";
        }
      }
    }
    return statusMessage;
  };

  return (
    <div className='min-h-screen w-full flex items-center justify-center bg-background p-4'>
      <Card className='w-full max-w-lg rounded-lg border border-border bg-card shadow-sm'>
        <CardContent className='pt-8 pb-6 px-6 sm:px-8'>
          <div className='flex items-start gap-4'>
            <div
              className='shrink-0 rounded-xl bg-destructive/10 p-3 text-destructive'
              aria-hidden>
              <ShieldAlert className='h-6 w-6' strokeWidth={1.75} />
            </div>
            <div className='min-w-0 flex-1'>
              <h1 className='text-xl font-semibold text-foreground leading-snug'>
                Access denied
              </h1>
              <p className='mt-3 text-sm text-muted-foreground leading-relaxed'>
                You were authenticated, but you are not authorized to use
                OneView. Below are some details to assist in troubleshooting:
              </p>
            </div>
          </div>

          {authStatusDetails ? (
            <div className='mt-6 rounded-lg border border-border bg-muted px-4 py-3 text-sm text-foreground leading-relaxed'>
              {authStatusDetails}
            </div>
          ) : null}

          <p className='mt-6 text-sm text-muted-foreground leading-relaxed'>
            You may use the button below to log out and return to the Detechtion
            sign-in page.
          </p>

          <div className='mt-8'>
            <Button
              type='button'
              onClick={handleLogout}
              variant='ghost'
              size='sm'
              className='w-full justify-center font-light text-sm border border-destructive text-destructive hover:bg-destructive hover:!text-destructive-foreground hover:[&_svg]:!text-destructive-foreground transition-colors'>
              <LogOut className='h-4 w-4 mr-2' />
              Log Out
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
